// ─────────────────────────────────────────────────────────────────────────────
// D-5 — Fuente única de los permisos del Programa de Reconocimiento en el frontend.
//
// Antes había tres comparaciones sueltas, todas escritas ANTES del catálogo de seis roles
// (SharedContracts `681185f`, aprobado por el cliente el 30-jul-2026) y todas equivocadas del
// mismo modo — comprobaban `role === 'AdminOU' || role === 'AdminGlobal'`, de manera que
// `AdminBrillamos` y `AdminOUBrillamos`, que son justamente quienes administran el programa, no
// pasaban ninguna:
//
//   ReconocerView.tsx:74        canBulk
//   ReconocerPanel.tsx:142      canDelegate
//   access/accessShared.tsx:46  isAdmin
//
// Además leían `useEffectiveRole()`, que devuelve `primaryRole` — documentado como etiqueta de
// UI, no como criterio de acceso. Aquí se usa la lista completa de roles.
//
// El backend sigue siendo la autoridad: revalida `bulkCsv` y la delegación y responde 403/422.
// Esto solo evita ofrecer al usuario acciones que el servidor le va a rechazar.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffectiveRoles } from '@/auth/RoleGuard';
import { canAccessRecognitionAdminSection } from '@/auth/accessMatrix';
import { useUserContext, isRecognitionAdmin } from '@/contexts/UserContext';
import { useGdprOuKey } from '@/hooks/useOUs';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useRecognitionRules } from '@/api/recognition';

export interface RecognitionPermissions {
  /** Administra el Programa de Reconocimiento (AdminGlobal / AdminBrillamos / AdminOUBrillamos). */
  isAdmin: boolean;
  /** Puede enviar "a nombre de" otra persona. */
  canDelegate: boolean;
  /** Puede usar la carga masiva por CSV. Se oculta bajo restricción GDPR. */
  canBulk: boolean;
  /** La OU del usuario está marcada como GDPR (antes: "es Europa"). */
  isGdprRestricted: boolean;
  /**
   * Tiene la capacidad de Reportería (`viewReports`). Gatea las consultas a
   * `/v1/analytics/recognition/*`, que el backend exige con `RecognitionCapability.Reporteria`
   * (`AnalyticsService/.../RecognitionAnalyticsController.cs`) y responde **403** a quien no la
   * tenga. Sin esto, la pantalla de Brillamos pedía el reporte de campaña a CUALQUIER empleado y se
   * comía un 403 por render (medido en QA con un colaborador Frontline: `permissions: []` → 403).
   */
  canViewReports: boolean;
}

export function useRecognitionPermissions(programa?: string | null): RecognitionPermissions {
  const roles = useEffectiveRoles();
  const { demoUser } = useUserContext();
  const gdprOuKey = useGdprOuKey();
  const currentUser = useCurrentUser();
  // `programa` (Brillamos lo pasa explícito) resuelve `bulkCsvEnabled` del programa correcto: sin él,
  // el botón de carga masiva CSV de la pantalla de Brillamos leía el toggle de Fundamentales e ignoraba
  // el override de Brillamos. El resto de permisos (isAdmin/canDelegate) no dependen de las reglas.
  const rules = useRecognitionRules(undefined, programa);

  // La condición de Europa dejó de ser un rol: se deriva del flag GDPR de la unidad organizacional
  // (`OrganisationalUnit.Gdpr`) — ver la nota del catálogo en `contexts/UserContext.tsx`.
  //
  // `demoUser.tenant` NO sirve para esto aunque lo parezca: es el selector de TENANT de backend
  // ('LATAM' | 'Europa', dos despliegues distintos) derivado del dominio del correo, y se necesita
  // antes de poder pedir las OUs — ver la nota de la dependencia circular en UserContext.tsx:253.
  // Se conserva como respaldo mientras el catálogo de OUs no haya cargado, para no dejar la
  // restricción sin fuente durante el primer render.
  const isGdprRestricted = gdprOuKey !== undefined
    ? demoUser.ou === gdprOuKey
    : demoUser.tenant === 'Europa';

  const isAdmin = isRecognitionAdmin(roles);

  // Misma puerta que usa el menú de Administración para la sección "reporteria", en vez de un
  // chequeo nuevo: `accessMatrix` ya es el espejo de `RolePermissionMatrix` de SharedContracts y
  // resuelve el respaldo por rol cuando el perfil todavía no trae permisos. Duplicar el criterio
  // aquí es exactamente lo que este módulo existe para evitar (ver cabecera).
  const canViewReports = canAccessRecognitionAdminSection(
    'reporteria',
    roles,
    currentUser?.recognitionPermissions ?? null,
  );

  // D-5 (delegación por permiso, no solo por rol): requiere admin CON el permiso granular
  // delegateRecognition asignado en su rol — ver PermissionKeys.Order / AdminRoleEditorModal.tsx.
  // `currentUser.recognitionPermissions` es el catálogo canónico ya resuelto del backend; `null`
  // mientras el perfil carga se trata como "sin permisos" (fail-closed).
  const grantedPermissions = currentUser?.recognitionPermissions ?? [];
  const canDelegate = isAdmin && grantedPermissions.includes('delegateRecognition');

  // Espejo del toggle de Gestión (RulesEngine): `bulkCsvEnabled` apaga la carga masiva para el
  // programa completo. `true` es el respaldo mientras las reglas no han resuelto, para no ocultar
  // la acción de golpe durante el primer render (el backend revalida igual, ver nota del módulo).
  const bulkCsvEnabled = rules.data?.bulkCsvEnabled ?? true;
  // El botón de carga masiva por CSV (pantalla Reconocer Y Brillamos Juntos, ambas vía `canBulk`)
  // exige además el permiso granular `bulkCsv` del usuario: si su rol no lo tiene concedido, el botón
  // no aparece. Antes bastaba con ser admin + toggle global; ahora también depende del permiso por
  // administrador que se edita en el modal de Roles y Permisos. El backend revalida igual.
  const canBulk = isAdmin && grantedPermissions.includes('bulkCsv') && !isGdprRestricted && bulkCsvEnabled;

  return {
    isAdmin,
    canDelegate,
    canBulk,
    isGdprRestricted,
    canViewReports,
  };
}
